import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'next-i18next'

interface Props {
  onConfirm: () => void
}

type IosVideo = HTMLVideoElement & { webkitDisplayingFullscreen?: boolean; webkitExitFullscreen?: () => void }

/**
 * A fullscreen element sits above everything, the prompt included, and `inert` would freeze it: a learner would come
 * back to a paused fullscreen video that ignores them, with the prompt hidden behind it.
 */
function exitFullscreen(): void {
  if (document.fullscreenElement) {
    document.exitFullscreen().catch(() => undefined)
  }
  // iOS plays fullscreen video in its own player, outside the page and its Fullscreen API.
  document.querySelectorAll('video').forEach((video: IosVideo) => {
    if (video.webkitDisplayingFullscreen) {
      video.webkitExitFullscreen?.()
    }
  })
}

/**
 * Shown once the learner has been idle for 30 minutes (`useUserPulseTracker`). Time spent stays paused until they
 * answer it, so input alone, like the mouse brushing the desk, doesn't start the count again.
 *
 * It renders beside the app root and makes the app inert, so keyboard focus can't slip onto the page behind it:
 * work done there while the count is paused would go uncounted.
 */
export default function StillThereModal({ onConfirm }: Props): React.ReactElement {
  const { t } = useTranslation('common')
  const buttonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    exitFullscreen()
    const appRoot = document.getElementById('__next')
    appRoot?.setAttribute('inert', '')
    buttonRef.current?.focus()

    return () => {
      appRoot?.removeAttribute('inert')
    }
  }, [])

  return createPortal(
    <aside className="fixed inset-0 z-top flex items-center justify-center bg-black/60 p-6">
      <section
        aria-labelledby="still-there-title"
        aria-modal="true"
        className="flex w-full max-w-110 flex-col items-center gap-3 overflow-hidden rounded-2 border-2 border-purple-half-deep bg-gradient-purple-dark px-6 pb-6 pt-7 text-center text-white shadow-2xl md:gap-4 md:px-8.5 md:pb-8 md:pt-10"
        data-test-id="still-there-modal"
        role="dialog"
      >
        <span
          aria-hidden
          className="flex size-21 shrink-0 items-center justify-center rounded-full border-3 border-white bg-gradient-violet-bright text-13 font-black leading-none shadow-violet-glow-black-bottom-right md:size-26 md:text-16"
        >
          ?
        </span>
        <h2 className="text-2xl font-bold leading-7.5 md:text-3xl md:leading-9" id="still-there-title">
          {t('stillThere.title')}
        </h2>
        <p className="text-base leading-6 text-white/90 md:text-md md:leading-7">{t('stillThere.body')}</p>
        {/* The design's spacer sits between two gaps: 12 + 4 + 12 px, and 16 + 8 + 16 px from md. */}
        <button
          className="mt-4 flex h-13 w-full shrink-0 items-center justify-center rounded-4xl border border-white bg-gradient-purple-violet px-4 text-md font-black leading-6.5 shadow-black-bottom-right transition hover:opacity-90 md:mt-6"
          data-test-id="still-there-confirm"
          onClick={onConfirm}
          ref={buttonRef}
          type="button"
        >
          {t('stillThere.cta')}
        </button>
      </section>
    </aside>,
    document.body,
  )
}
